import { Box, Popover, styled } from '@mui/material'
import { FormTemplate } from 'ComponentLibrary/src/templates/FormTemplate'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { BaseSyntheticEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'

type EditableCellProps = {
    displayCell: ReactNode
    children: ReactNode
    size?: 'small' | 'medium'
    onSubmit: (event?: BaseSyntheticEvent) => void | Promise<void>
    reset: () => void
    shouldDisableSubmit: boolean
    textAlign?: 'start' | 'end' | 'center'
    disabled?: boolean
    readOnly?: boolean
    inline?: boolean
    validate: () => Promise<boolean>
}

export default function EditableCell({
    displayCell,
    size,
    children,
    reset,
    onSubmit,
    shouldDisableSubmit,
    textAlign = 'start',
    disabled,
    readOnly = false,
    inline = false,
    validate,
}: EditableCellProps) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const restoringFocus = useRef(false)
    const nextFocus = useRef<HTMLElement | null>(null)

    useEffect(() => {
        if (!open) {
            // Move after the popover releases its focus trap.
            nextFocus.current?.focus()
            nextFocus.current = null
        } else if (inline) {
            const input = ref.current?.querySelector<HTMLInputElement>('input, textarea')
            input?.focus()
            input?.select()
        }
    }, [inline, open])

    const onClose = () => {
        restoringFocus.current = !inline
        reset()
        setOpen(false)
    }

    const onSubmitAndClose = async (event?: BaseSyntheticEvent) => {
        if (shouldDisableSubmit) {
            onClose()
        } else if (await validate()) {
            restoringFocus.current = true
            setOpen(false)
            void onSubmit(event)
        }
    }

    const onOpen = () => {
        if (!disabled && !readOnly) {
            reset()
            setOpen(true)
        }
    }

    const saveInline = async () => {
        if (shouldDisableSubmit) {
            onClose()
        } else if (await validate()) {
            await onSubmit()
            setOpen(false)
        }
    }

    const moveFocusVertically = async (event: KeyboardEvent) => {
        if (
            !['ArrowUp', 'ArrowDown'].includes(event.key) ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.nativeEvent.isComposing ||
            !event.currentTarget.contains(event.target as Node)
        ) {
            return
        }

        const cell = ref.current?.closest('td')
        if (!cell) {
            return
        }

        event.preventDefault()
        event.stopPropagation()
        const direction = event.key === 'ArrowUp' ? 'previousElementSibling' : 'nextElementSibling'
        let row = cell.parentElement?.[direction]
        while (row) {
            const nextControl = row.children[cell.cellIndex]?.querySelector<HTMLElement>(
                '[tabindex="0"], input:not([type="hidden"]):not(:disabled), textarea:not(:disabled)'
            )
            if (nextControl && !nextControl.closest('[inert]')) {
                if (open && !inline) {
                    if (!shouldDisableSubmit) {
                        if (!(await validate())) {
                            return
                        }
                        void onSubmit()
                    }
                    nextFocus.current = nextControl
                    restoringFocus.current = true
                    setOpen(false)
                } else {
                    nextControl.focus()
                }
                return
            }
            row = row[direction]
        }
    }

    if (inline && open) {
        return (
            <InlineCell
                ref={ref}
                $open
                $textAlign={textAlign}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        void saveInline()
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        event.preventDefault()
                        event.stopPropagation()
                        onClose()
                    } else if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                        event.preventDefault()
                        ;(event.target as HTMLElement).blur()
                    } else {
                        void moveFocusVertically(event)
                    }
                }}
            >
                <Box aria-hidden sx={{ visibility: 'hidden', width: '100%' }}>
                    {displayCell}
                </Box>
                {children}
            </InlineCell>
        )
    }

    return (
        <>
            <StyledCell
                $open={open}
                sx={inline ? { '& > *': { width: '100%' } } : undefined}
                role="button"
                tabIndex={!disabled && !readOnly ? 0 : undefined}
                $disabled={disabled}
                $readOnly={readOnly}
                onClick={onOpen}
                onFocus={() => {
                    if (!restoringFocus.current) {
                        onOpen()
                    }
                    restoringFocus.current = false
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onOpen()
                    } else {
                        void moveFocusVertically(event)
                    }
                }}
                ref={ref}
                $textAlign={textAlign}
            >
                {displayCell}
            </StyledCell>
            <Popover
                open={open}
                anchorEl={ref.current}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    transition: {
                        onEntered: (element) => element.querySelector<HTMLInputElement>('input:not([type="hidden"]), textarea')?.focus(),
                    },
                    paper: {
                        onKeyDownCapture: (event) => {
                            if (
                                event.key === 'Enter' &&
                                !event.nativeEvent.isComposing &&
                                event.currentTarget.contains(event.target as Node) &&
                                (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
                            ) {
                                event.preventDefault()
                                event.stopPropagation()
                                void onSubmitAndClose()
                            } else {
                                void moveFocusVertically(event)
                            }
                        },
                        style: {
                            padding: tokens.SizingSpacing150,
                            paddingTop: `calc(${tokens.SizingSpacing150} + ${tokens.SizingSpacing100})`,
                            marginTop: tokens.SizingSpacing025,
                            width: size === 'small' ? '200px' : '300px',
                        },
                    },
                }}
            >
                <FormTemplate denseFooter onSubmit={onSubmitAndClose} onCancel={onClose} shouldDisableSubmit={shouldDisableSubmit}>
                    {children}
                </FormTemplate>
            </Popover>
        </>
    )
}

const StyledCell = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $open?: boolean; $disabled?: boolean; $textAlign: 'start' | 'end' | 'center'; $readOnly?: boolean }>(
    ({ $open, $textAlign, $disabled, $readOnly }) => ({
        position: 'relative',
        isolation: 'isolate',
        padding: `var(--table-cell-padding-block, ${tokens.SizingSpacing100}) var(--table-cell-padding-inline, ${tokens.SizingSpacing200})`,
        marginBlock: `calc(-1 * var(--table-cell-padding-block, ${tokens.SizingSpacing100}))`,
        marginInline: `calc(-1 * var(--table-cell-padding-inline, ${tokens.SizingSpacing200}))`,
        textAlign: $textAlign,
        justifyContent: $textAlign,
        boxSizing: 'content-box',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        borderRadius: tokens.SizingCornerS,
        '&::before, &::after': {
            content: '""',
            position: 'absolute',
            inset: tokens.SizingSpacing050,
            borderRadius: 'inherit',
            pointerEvents: 'none',
        },
        '&::before': {
            zIndex: -1,
        },
        '&::after': {
            border: `${tokens.SizingBorderS} solid ${$open ? tokens.ColorBorderFocused : 'transparent'}`,
            zIndex: 1,
        },
        '&:hover': {
            cursor: 'pointer',
            '&::before': { backgroundColor: tokens.ColorSurfaceHover },
            ...(!$open ? { '&::after': { borderColor: tokens.ColorBorderDefault } } : {}),
        },
        ...($readOnly ? { pointerEvents: 'none' } : {}),
        ...($disabled ? { pointerEvents: 'none', opacity: 0.75 } : {}),
    })
)

const InlineCell = styled(StyledCell)({
    '& > .MuiGridLegacy-root': {
        position: 'absolute',
        inset: 0,
        display: 'flex',
    },
    '& .MuiFormControl-root, & .MuiInputBase-root, & .MuiInputAdornment-root .MuiTypography-root': {
        font: 'inherit',
    },
    '& .MuiInputBase-root': {
        height: '100%',
        margin: 0,
        padding: `0 var(--table-cell-padding-inline, ${tokens.SizingSpacing200})`,
        color: 'inherit',
    },
    '& .MuiInputBase-input': {
        padding: '0!important',
        height: 'auto!important',
        minWidth: 0,
    },
    '& .MuiInputAdornment-root': {
        marginLeft: tokens.SizingSpacing050,
    },
    '& .MuiInputAdornment-root:empty, & .MuiOutlinedInput-notchedOutline': {
        display: 'none',
    },
    '&:has(.MuiInputBase-root.Mui-error)::after': {
        borderColor: tokens.ColorBorderDanger,
    },
    '& .MuiFormHelperText-root': {
        bottom: 0,
        right: `var(--table-cell-padding-inline, ${tokens.SizingSpacing200})`,
        margin: 0,
    },
})
